/**
 * Language Slice
 *
 * Zustand slice for language state. Supported languages come from the backend
 * (GET /public/supported-languages); no duplicate list on the frontend.
 */

import type { SliceCreator } from '../types';

// ============================================================================
// Types
// ============================================================================

/** Language code (backend is source of truth; use string to accept any code from API). */
export type SupportedLanguage = string;

/** Writing direction for a UI language (drives `<html dir>`). */
export type LanguageDirection = 'ltr' | 'rtl';

/** One language option from the API (code, name, nativeName, flag, isDefault, dir). */
export interface LanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isDefault: boolean;
  dir: LanguageDirection;
}

/** Default when no list has been loaded yet (bootstrap only). */
export const DEFAULT_LANGUAGE = 'en';

// ============================================================================
// State & Actions
// ============================================================================

export interface LanguageSliceState {
  currentLanguage: SupportedLanguage;
  languageInitialized: boolean;
  /** From GET /public/supported-languages (backend source of truth). Null until fetched. */
  supportedLanguagesList: LanguageInfo[] | null;
}

export interface LanguageSliceActions {
  setLanguage: (language: SupportedLanguage) => void;
  setSupportedLanguages: (list: LanguageInfo[] | null) => void;
}

export type LanguageSlice = LanguageSliceState & LanguageSliceActions;

// ============================================================================
// Initial State
// ============================================================================

export const languageSliceInitialState: LanguageSliceState = {
  currentLanguage: DEFAULT_LANGUAGE,
  languageInitialized: false,
  supportedLanguagesList: null,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createLanguageSlice: SliceCreator<LanguageSlice> = (set, _get) => ({
  ...languageSliceInitialState,

  setLanguage: (language) => {
    set(
      { currentLanguage: language },
      false,
      'language/setLanguage'
    );
  },

  setSupportedLanguages: (list) => {
    set(
      { supportedLanguagesList: list },
      false,
      'language/setSupportedLanguages'
    );
  },
});
